import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom, Observable }                   from 'rxjs';
import { TranslateService }                            from '@ngx-translate/core';

import { API }               from './api.constant';
import { CacheService }      from './cache.service';
import { ENCODINGS }         from './api.encodings.constant';
import { JsonService }       from '../core/services/json.service';
import { ModalService }      from '../modals/modal.service';
import { PruneService }      from '../core/services/prune.service';
import { SessionService }    from '../sessions/session.service';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  _API       = API;
  _ENCODINGS = ENCODINGS;
  _isAlerted = false;
  resolving  = false;

  constructor (
    private httpClient      : HttpClient,
    private translateService: TranslateService,
    private cacheService    : CacheService,
    private jsonService     : JsonService,
    private modalService    : ModalService,
    private pruneService    : PruneService,
    private sessionService  : SessionService
  ) {}

  private _convertToPromise ( call : Observable<any> ) : Promise<any> {


    return lastValueFrom(call);


  }

  private _formatUrl ( url : string ) : string {


    return `${ this._API.url }${ url.charAt(0) === '/' ? '': '/' }${ url }`;


  }

  private _isParamEmpty ( param : any ) : boolean {


    return param === undefined
      || param === null
      || param === '';


  }

  private _validateSession ( error : HttpErrorResponse ) : void {
    let message = 'You have been logged out.';

    if ( !this._isAlerted && this.sessionService.isSessionError(error) ) {
      this._isAlerted = true;

      if (this.sessionService.isSessionExpired(error)) {
        message = this.translateService.instant('JS_SPACE.MESSAGES.SESSION.EXPIRED');
      }

      else if (this.sessionService.isSessionDeactivated(error)) {
        message = this.translateService.instant('JS_SPACE.MESSAGES.SESSION.DEACTIVATED');
      }

      this.sessionService.clear();
      window.location.assign('login.html');

      this.modalService
        .alert({
          message,
          title: 'Session Error'
        })
        .finally(() => this._isAlerted = false);
    }
  }

  delete ( url : string, id ?: string ) : Promise<any> {
    if (id) {

      url = `${ url }/${ this.encode(id) }`;

    }

    return this._convertToPromise(this.httpClient.delete(encodeURI(this._formatUrl(url)), { withCredentials: true }))
    .catch(( response : HttpErrorResponse ) => {
      this._validateSession(response);

      return Promise.reject(response.error);
    });
  }

  encode ( param : any, encodeFor = 'rails' ) : string {
    const replaces = this._ENCODINGS.base.concat(this._ENCODINGS[ encodeFor ]);

    for ( let i = 0; i < replaces.length; i++ ) {


      param = param.replace(replaces[ i ].character, replaces[ i ].encoding);


    }

    return param;
  }

  get ( url : string, params ?: any, cacheObj ?: string, options : any = {} ) : Promise<any> {
    const cached = this.cacheService.get(cacheObj);

    this.resolving = true;

    if (params || params === false || params === 0) {
      if ( !(params instanceof Array) ) {
        params = [ params ];
      }

      for ( let i = 0; i < params.length; i++ ) {
        if (!this._isParamEmpty(params[i])) {
          url = `${ url }/${ this.encode( params[i].toString(), 'get') }`;
        }
      };
    }

    if (cached) {


      this.resolving = false;


      return Promise.resolve(cached);


    }

    if (!options.skipEncoding) {


      url = encodeURI(url);


    }

    options.observe         = 'response';
    options.withCredentials = true;

    return this._convertToPromise(this.httpClient.get(this._formatUrl(url), options))
    .then(( response : HttpResponse<any> ) => {
      if (cacheObj) {

        this.cacheService.put(cacheObj, response.body);

      }

      return options.verbose ? response : response.body;
    })
    .catch(( response : HttpErrorResponse ) => {
      this._validateSession(response);

      return Promise.reject(options.verbose ? response : response.error);
    })
    .finally(() => {
      this.resolving = false;
    });
  }

  getEndpoint () : string {


    return this._API.url;


  }

  patch ( url : string, params : any = {}, name ?: string, cleanup ?: string, options : any = {} ) : Promise<any> {
    let body = {};

    this.resolving = true;

    if (name) {


      body[name] = params;


    }

    // If the parameters need to be flat
    else {
      body = params;
    }

    if (cleanup) {


      body[name][cleanup] = this.pruneService.removeUntouched(body[name][cleanup]);


    }

    options.observe         = 'response';
    options.withCredentials = true;

    return this._convertToPromise(this.httpClient.patch(this._formatUrl(url), body, options))
    .then(( response : HttpResponse<any> ) => options.verbose ? response : response.body)
    .catch(( response : HttpErrorResponse ) => {
      this._validateSession(response);

      return Promise.reject(options.verbose ? response : response.error);
    })
    .finally(() => {
      this.resolving = false;
    });
  }

  post ( url : string, params : any = {}, name ?: string, cleanup ?: string | boolean, options : any = {} ) : Promise<any> {
    let body = {};

    this.resolving = true;

    // If the parameters need to be nested from the session id
    if (name) {
      body[name] = params;

      if (typeof cleanup === 'string') {


        body[name][cleanup] = this.pruneService.removeUntouched(body[name][cleanup]);


      }
      else if (cleanup) {
        body[name] = this.pruneService.removeUntouched(body[name]);
      }
    }

    // If the parameters need to be flat
    else {
      body = params;
    }

    options.observe         = 'response';
    options.withCredentials = true;

    return this._convertToPromise(this.httpClient.post(this._formatUrl(url), body, options))
    .then(( response : HttpResponse<any> ) => options.verbose ? response : response.body)
    .catch(( response : HttpErrorResponse ) => {
      this._validateSession(response);

      return Promise.reject(options.verbose ? response : response.error);
    })
    .finally(() => {
      this.resolving = false;
    });
  }

  stream ( url : string, params ?: any, options : any = {} ) : any {
    let stream : EventSource;

    const promise = new Promise(( resolve, reject ) => {
      if ( params && !( params instanceof Array ) ) {
        params = [ params ];
      }

      else if ( params ) {
        for ( let i = 0; i < params.length; i++ ) {

          url = `${ url }/${ this.encode( params[i].toString() ) }`;

          };

        }

      stream = new EventSource(encodeURI(this._formatUrl(url)), { withCredentials: true });

      stream.addEventListener('message', ( event : MessageEvent ) => {
        const data = this.jsonService.parse(event.data);

        if (!data.length) {


          stream.close();


          resolve([]);


        }
        else {
          resolve(data);
        }
      });

      stream.addEventListener('error', ( event : MessageEvent ) => {
        stream.close();

        resolve([]);
      });
    });

    if ( options.verbose ) {
      return {
        promise,
        stream
      };
    }

    return promise;
  }
}
